import { useState } from "react";
import { ArrowLeftRight, Search } from "lucide-react";
import { toast } from "sonner";
import { AIRPORTS } from "../../lib/airports";
import { buildBookingUrl, track } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { LeadCaptureDialog } from "./LeadCaptureDialog";

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const AirportSelect = ({ value, onChange, label, testId }) => (
  <div className="flex-1 min-w-0">
    <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">{label}</label>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent text-sm font-semibold text-slate-900 focus:outline-none cursor-pointer"
    >
      {AIRPORTS.map((a) => (
        <option key={a.code} value={a.code}>
          {a.city} ({a.code})
        </option>
      ))}
    </select>
  </div>
);

export const SearchBox = ({ defaultDep = "KHI", defaultRet = "DXB", page = "search", lockAirports = false }) => {
  const { settings } = useSettings();
  const daysAhead = Number(settings?.offer_days_ahead) || 2;
  const stayDays = Number(settings?.offer_stay_days) || 7;
  const [tripType, setTripType] = useState("roundtrip");
  const [dep, setDep] = useState(defaultDep);
  const [ret, setRet] = useState(defaultRet);
  const [departDate, setDepartDate] = useState(todayPlus(daysAhead));
  const [returnDate, setReturnDate] = useState(todayPlus(daysAhead + stayDays));
  const [adult, setAdult] = useState(1);
  const [child, setChild] = useState(0);
  const [infant, setInfant] = useState(0);
  const [cabin, setCabin] = useState("Y");
  const [direct, setDirect] = useState(false);
  const [showLead, setShowLead] = useState(false);

  const swap = () => {
    setDep(ret);
    setRet(dep);
  };

  const onSearch = () => {
    if (dep === ret) {
      toast.error("Departure and arrival airports must be different");
      return;
    }
    const params = { tripType, dep, ret, departDate, returnDate, adult, child, infant, cabin, direct, langcode: settings?.langcode || "EN" };
    track("search_initiated", page, params);
    const url = buildBookingUrl(settings?.booking_domain, params);
    track("booking_handoff", page, { url });
    window.open(url, "_blank", "noopener");
    toast.success("Opening live fares in the booking engine...");
    setTimeout(() => setShowLead(true), 2500);
  };

  const inputCls = "w-full bg-transparent text-sm font-semibold text-slate-900 focus:outline-none cursor-pointer";
  const cellCls = "bg-white border border-slate-200 rounded-xl px-4 py-3";

  return (
    <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-2xl shadow-xl shadow-slate-900/5 p-4 sm:p-6" data-testid="flight-search-box" id="search-box">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { v: "roundtrip", l: "Round Trip" },
          { v: "oneway", l: "One Way" },
        ].map((t) => (
          <button
            key={t.v}
            data-testid={`trip-type-${t.v}`}
            onClick={() => setTripType(t.v)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors duration-200 ${
              tripType === t.v ? "bg-[#970C10] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {t.l}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            data-testid="direct-flights-checkbox"
            checked={direct}
            onChange={(e) => setDirect(e.target.checked)}
            className="accent-[#970C10] w-4 h-4"
          />
          Direct flights only
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className={`md:col-span-4 ${cellCls} flex items-center gap-2`}>
          <AirportSelect value={dep} onChange={setDep} label="From" testId="search-from-select" />
          <button
            data-testid="search-swap-btn"
            onClick={swap}
            disabled={lockAirports}
            className="shrink-0 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors duration-200 disabled:opacity-40"
            aria-label="Swap airports"
          >
            <ArrowLeftRight className="w-3.5 h-3.5 text-slate-600" />
          </button>
          <AirportSelect value={ret} onChange={setRet} label="To" testId="search-to-select" />
        </div>
        <div className={`md:col-span-3 ${cellCls} grid grid-cols-2 gap-3`}>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Depart</label>
            <input type="date" data-testid="search-depart-date" min={todayPlus(0)} value={departDate} onChange={(e) => setDepartDate(e.target.value)} className={inputCls} />
          </div>
          <div className={tripType === "oneway" ? "opacity-40 pointer-events-none" : ""}>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Return</label>
            <input type="date" data-testid="search-return-date" min={departDate} value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div className={`md:col-span-3 ${cellCls} grid grid-cols-4 gap-2`}>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Adult</label>
            <select data-testid="search-adult-select" value={adult} onChange={(e) => setAdult(+e.target.value)} className={inputCls}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Child</label>
            <select data-testid="search-child-select" value={child} onChange={(e) => setChild(+e.target.value)} className={inputCls}>
              {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Infant</label>
            <select data-testid="search-infant-select" value={infant} onChange={(e) => setInfant(+e.target.value)} className={inputCls}>
              {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Class</label>
            <select data-testid="search-cabin-select" value={cabin} onChange={(e) => setCabin(e.target.value)} className={inputCls}>
              <option value="Y">Econ</option>
              <option value="W">Prem</option>
              <option value="C">Biz</option>
              <option value="F">First</option>
            </select>
          </div>
        </div>
        <button
          data-testid="search-submit-btn"
          onClick={onSearch}
          className="md:col-span-2 bg-[#00E5FF] hover:bg-[#33EBFF] text-black font-bold rounded-xl px-6 py-4 flex items-center justify-center gap-2 transition-colors duration-200 hover:shadow-lg"
        >
          <Search className="w-4 h-4" />
          <span className="text-sm">Search Fares</span>
        </button>
      </div>

      <LeadCaptureDialog open={showLead} onOpenChange={setShowLead} page={page} />
    </div>
  );
};

export const StickySearchCTA = ({ params }) => (
  <button
    data-testid="sticky-search-cta"
    onClick={() => document.getElementById("search-box")?.scrollIntoView({ behavior: "smooth", block: "center" })}
    className={`md:hidden fixed bottom-4 inset-x-4 z-40 font-bold py-3.5 rounded-full shadow-xl flex items-center justify-center gap-2 ${params ? "bg-[#0B1527] text-white border border-[#00E5FF]/40" : "bg-[#00E5FF] text-black"}`}
  >
    {params ? (
      <span className="font-mono text-sm tracking-wide">
        {params.dep} → {params.ret} · {new Date(params.departDate).toLocaleDateString(undefined, { day: "numeric", month: "short" })} · {Number(params.adult || 1) + Number(params.child || 0)} pax
      </span>
    ) : (
      <><Search className="w-4 h-4" /> Search Cheap Flights</>
    )}
  </button>
);
